import ScButton from "@/src/components/ui-kit/button";
import InputField from "@/src/components/ui-kit/input-field.rhf";
import type { CategoryTag } from "@/src/models/category.model";
import { useTheme } from "@/src/styles/theme-provider";
import { CloseCircle } from "iconsax-react-nativejs";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Modal, Pressable, Text, View } from "react-native";

type AddCategoryModalProps = Readonly<{
  visible: boolean;
  onAdd: (category: string, tag: CategoryTag | null) => void;
  onCancel: () => void;
  loading?: boolean;
  fixedTag?: CategoryTag;
}>;

type CategoryFormValues = Readonly<{
  category: string;
}>;

type TagOption = Readonly<{
  labelKey: string;
  value: CategoryTag;
}>;

const CATEGORY_MAX_LENGTH = 20;

const AVAILABLE_TAGS: readonly TagOption[] = [
  { labelKey: "LABEL_EVENTS", value: "events" },
  { labelKey: "LABEL_WIKI", value: "wiki" },
  { labelKey: "LABEL_NEWS", value: "news" },
];

export default function AddCategoryModal({
  visible,
  onAdd,
  onCancel,
  loading = false,
  fixedTag,
}: AddCategoryModalProps) {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();
  const [selectedTag, setSelectedTag] = useState<CategoryTag | null>(null);

  const showTagPicker = !fixedTag;

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<CategoryFormValues>({
    defaultValues: { category: "" },
  });

  const categoryValue = watch("category");
  const isOverLimit = categoryValue.length > CATEGORY_MAX_LENGTH;
  const isAddDisabled = isOverLimit || !categoryValue.trim();

  const errorMessage = isOverLimit
    ? `${t("ERROR_MAX_LENGTH")} ${CATEGORY_MAX_LENGTH}`
    : errors.category?.message;

  const handleToggleTag = (tag: CategoryTag) => {
    setSelectedTag((previousTag) => (previousTag === tag ? null : tag));
  };

  const handleAddCategory = (formValues: CategoryFormValues) => {
    const trimmedCategory = formValues.category.trim();
    if (!trimmedCategory || isOverLimit) return;
    onAdd(trimmedCategory, fixedTag ?? selectedTag);
    reset();
    setSelectedTag(null);
  };

  const handleCancel = () => {
    reset();
    setSelectedTag(null);
    onCancel();
  };

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={handleCancel}
    >
      <Pressable
        style={varStyle}
        className="flex-1 bg-shadow-dark items-center justify-center px-6"
        onPress={handleCancel}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          className="bg-ash rounded-2xl w-full max-w-md gap-5 p-6"
        >
          {/* Header */}
          <View className="flex-row justify-between items-center">
            <Text className="text-lg font-semibold text-onyx">
              {t("LABEL_NEW_CATEGORY")}
            </Text>

            <Pressable
              onPress={handleCancel}
              className="p-1.5 rounded-full hover:bg-ash-3"
            >
              <CloseCircle
                size={22}
                color={themeVars["--color-slate-default"]}
              />
            </Pressable>
          </View>
          <View>
            <InputField
              control={control}
              name="category"
              label={t("LABEL_NEW_CATEGORY")}
              placeholder={t("HINT_ENTER_CATEGORY_NAME")}
              error={errorMessage}
              returnKeyType="done"
              blurOnSubmit={false}
              onSubmitEditing={() => {
                if (isAddDisabled || loading) return;
                handleSubmit(handleAddCategory)();
              }}
            />
          </View>
          {showTagPicker && (
            <View className="gap-2">
              <Text className="text-sm font-medium text-slate">
                {t("LABEL_TAGS")}
              </Text>

              <View className="flex-row flex-wrap gap-2">
                {AVAILABLE_TAGS.map((tagOption) => {
                  const isSelected = selectedTag === tagOption.value;

                  return (
                    <Pressable
                      key={tagOption.value}
                      onPress={() => handleToggleTag(tagOption.value)}
                      className={`px-4 py-1.5 rounded-full md:border-2 ${
                        isSelected
                          ? "bg-primary-2 md:border-primary hover:bg-primary-2"
                          : "bg-chalk md:bg-ash md:border-ash-3 hover:bg-ash-3"
                      }`}
                    >
                      <Text
                        className={`text-sm font-medium ${
                          isSelected ? "text-primary" : "text-slate"
                        }`}
                      >
                        {t(tagOption.labelKey)}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </View>
          )}

          {/* Action Buttons */}
          <View className="flex-row gap-3 mt-1">
            <View className="flex-1">
              <ScButton
                text="LABEL_CANCEL"
                mode="outlined"
                onPress={handleCancel}
              />
            </View>

            <View className="flex-1">
              <ScButton
                text="LABEL_ADD"
                mode="contained"
                disabled={isAddDisabled}
                onPress={handleSubmit(handleAddCategory)}
                loading={loading}
              />
            </View>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
